import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { AddressBook, MagnifyingGlass, X, Plus, LinkedinLogo, PencilSimple } from "@phosphor-icons/react";

const STATUS_LABELS = {
  "non_contacté": "Non contacté", "dm_envoyé": "DM envoyé", "répondu_positif": "Réponse +",
  "répondu_négatif": "Réponse −", "silencieux": "Silencieux", "atelier_inscrit": "Atelier inscrit",
  "client": "Client", "perdu": "Perdu",
};
const STATUS_COLOR = {
  "non_contacté": "bg-zinc-500/15 text-zinc-300", "dm_envoyé": "bg-blue-500/15 text-blue-300",
  "répondu_positif": "bg-emerald-500/15 text-emerald-300", "répondu_négatif": "bg-orange-500/15 text-orange-300",
  "silencieux": "bg-amber-500/15 text-amber-300", "atelier_inscrit": "bg-violet-500/15 text-violet-300",
  "client": "bg-emerald-500 text-zinc-950", "perdu": "bg-red-500/15 text-red-300",
};

export default function Contacts() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [opts, setOpts] = useState({ statuses: [], channels: [], campaigns: [] });
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState({ search: "", status: "", campaign_tag: "", assignee_user_id: "" });
  const [edit, setEdit] = useState(null); // contact or {} for new

  const load = () => {
    setLoading(true);
    api.get("/contacts", { params: { search: f.search || undefined, status: f.status || undefined, campaign_tag: f.campaign_tag || undefined, assignee_user_id: f.assignee_user_id || undefined } })
      .then((r) => setRows(r.data)).catch(() => toast.error("Erreur de chargement")).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [f.status, f.campaign_tag, f.assignee_user_id]);
  useEffect(() => {
    api.get("/contacts-meta/options").then((r) => setOpts(r.data)).catch(() => {});
    api.get("/aeliam/team").then((r) => setTeam(r.data)).catch(() => {});
  }, []);

  return (
    <div className="p-8 max-w-[1400px] mx-auto space-y-6" data-testid="contacts-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-full bg-[#0a66c2] flex items-center justify-center"><AddressBook size={22} weight="fill" className="text-white" /></div>
          <div>
            <h1 className="font-head font-bold text-2xl">Contacts LinkedIn</h1>
            <p className="text-sm text-muted-foreground">Personnes physiques prospectées chez les cabinets · anti-doublon.</p>
          </div>
        </div>
        <button data-testid="contact-new-btn" onClick={() => setEdit({})} className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold aeliam-btn-primary">
          <Plus size={16} weight="bold" /> Nouveau contact
        </button>
      </div>

      <div className="bg-card border border-border p-4 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[200px]">
          <label className="overline text-muted-foreground">Recherche</label>
          <div className="flex items-center gap-2 border border-border px-3 py-2">
            <MagnifyingGlass size={16} className="text-muted-foreground" />
            <input data-testid="contact-search" value={f.search} onChange={(e) => setF({ ...f, search: e.target.value })} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Nom, cabinet…" className="flex-1 text-sm outline-none" />
          </div>
        </div>
        <div>
          <label className="overline text-muted-foreground">Statut</label>
          <select data-testid="contact-filter-status" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} className="block border border-border px-3 py-2 text-sm bg-card">
            <option value="">Tous</option>
            {opts.statuses.map((s) => <option key={s} value={s}>{STATUS_LABELS[s] || s}</option>)}
          </select>
        </div>
        <div>
          <label className="overline text-muted-foreground">Campagne</label>
          <select data-testid="contact-filter-campaign" value={f.campaign_tag} onChange={(e) => setF({ ...f, campaign_tag: e.target.value })} className="block border border-border px-3 py-2 text-sm bg-card">
            <option value="">Toutes</option>
            {opts.campaigns.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className="overline text-muted-foreground">Assigné à</label>
          <select data-testid="contact-filter-assignee" value={f.assignee_user_id} onChange={(e) => setF({ ...f, assignee_user_id: e.target.value })} className="block border border-border px-3 py-2 text-sm bg-card">
            <option value="">Tous</option>
            {team.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        </div>
      </div>

      <div className="bg-card border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-left"><tr className="overline text-muted-foreground">
            <th className="px-4 py-3">Contact</th><th className="px-4 py-3">Cabinet</th><th className="px-4 py-3">Statut</th>
            <th className="px-4 py-3">Assigné</th><th className="px-4 py-3">Campagne</th><th className="px-4 py-3">Dernière action</th><th className="px-4 py-3"></th>
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">Chargement…</td></tr>
              : rows.length === 0 ? <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">Aucun contact.</td></tr>
              : rows.map((c) => (
                <tr key={c.id} className="border-t border-border hover:bg-secondary/40" data-testid={`contact-row-${c.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium flex items-center gap-1.5">{c.full_name}
                      {c.linkedin_url && <a href={c.linkedin_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}><LinkedinLogo size={15} className="text-[#0a66c2]" weight="fill" /></a>}</div>
                    {c.headline && <div className="text-xs text-muted-foreground truncate max-w-[220px]">{c.headline}</div>}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{c.cabinet_nom || "—"}</td>
                  <td className="px-4 py-3"><span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${STATUS_COLOR[c.status] || "bg-gray-100"}`}>{STATUS_LABELS[c.status] || c.status}</span></td>
                  <td className="px-4 py-3 text-muted-foreground">{c.assignee_name || "—"}</td>
                  <td className="px-4 py-3 text-muted-foreground">{c.campaign_tag || "—"}</td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">{c.last_message_snippet || (c.updated_at ? new Date(c.updated_at).toLocaleDateString("fr-FR") : "—")}</td>
                  <td className="px-4 py-3 text-right"><button data-testid={`contact-edit-${c.id}`} onClick={() => setEdit(c)} className="p-1.5 text-muted-foreground hover:text-foreground"><PencilSimple size={16} /></button></td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {edit && <ContactModal contact={edit} team={team} opts={opts} currentUser={user} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); api.get("/contacts-meta/options").then((r) => setOpts(r.data)); }} />}
    </div>
  );
}

function ContactModal({ contact, team, opts, currentUser, onClose, onSaved }) {
  const isNew = !contact.id;
  const [form, setForm] = useState({
    full_name: contact.full_name || "", linkedin_url: contact.linkedin_url || "", cabinet_nom: contact.cabinet_nom || "",
    cabinet_id: contact.cabinet_id || "", headline: contact.headline || "", location: contact.location || "",
    email: contact.email || "", phone: contact.phone || "", status: contact.status || "non_contacté",
    channel: contact.channel || "", campaign_tag: contact.campaign_tag || "",
    assignee_user_id: contact.assignee_user_id || currentUser?.id || "", last_message_snippet: contact.last_message_snippet || "",
  });
  const [saving, setSaving] = useState(false);
  const [cabResults, setCabResults] = useState([]);
  const upd = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const searchCab = async (q) => {
    upd("cabinet_nom", q);
    if (q.length < 2) return setCabResults([]);
    try { const { data } = await api.get("/cabinets", { params: { search: q } }); setCabResults(data.slice(0, 6)); }
    catch (e) { console.error("Recherche cabinets échouée", e); setCabResults([]); }
  };

  const save = async () => {
    if (!form.full_name.trim()) return toast.error("Nom requis");
    setSaving(true);
    const member = team.find((m) => m.id === form.assignee_user_id);
    const payload = { ...form, assignee_name: member?.name };
    try {
      if (isNew) await api.post("/contacts", payload);
      else await api.patch(`/contacts/${contact.id}`, payload);
      toast.success(isNew ? "Contact créé" : "Contact mis à jour"); onSaved();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409) toast.error((d?.message || "Doublon détecté") + (d?.contact ? ` (assigné à ${d.contact.assignee_name || "—"})` : ""));
      else toast.error(typeof d === "string" ? d : "Erreur");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="contact-modal" onClick={() => !saving && onClose()}>
      <div className="bg-card w-full max-w-lg border border-border max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-border flex items-center justify-between sticky top-0 bg-card">
          <span className="font-head font-bold">{isNew ? "Nouveau contact" : form.full_name}</span>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X size={18} /></button>
        </div>
        <div className="p-5 grid grid-cols-2 gap-3">
          <Field label="Nom complet *" v={form.full_name} set={(v) => upd("full_name", v)} testid="contact-fullname" />
          <Field label="URL LinkedIn" v={form.linkedin_url} set={(v) => upd("linkedin_url", v)} testid="contact-linkedin" />
          <div className="col-span-2 relative">
            <Field label="Cabinet (recherche)" v={form.cabinet_nom} set={searchCab} testid="contact-cabinet" />
            {cabResults.length > 0 && (
              <div className="absolute z-10 bg-card border border-border w-full shadow-md">
                {cabResults.map((c) => (
                  <button key={c.id} type="button" onClick={() => { upd("cabinet_id", c.id); upd("cabinet_nom", c.nom); setCabResults([]); }}
                    className="block w-full text-left px-3 py-2 text-sm hover:bg-secondary">{c.nom} <span className="text-muted-foreground text-xs">· {c.ville} (ICP {c.icp?.score})</span></button>
                ))}
              </div>
            )}
          </div>
          <Field label="Headline (bio)" v={form.headline} set={(v) => upd("headline", v)} testid="contact-headline" />
          <Field label="Localisation" v={form.location} set={(v) => upd("location", v)} />
          <Field label="Email" v={form.email} set={(v) => upd("email", v)} />
          <Field label="Téléphone" v={form.phone} set={(v) => upd("phone", v)} />
          <div>
            <label className="overline text-muted-foreground">Statut</label>
            <select data-testid="contact-status" value={form.status} onChange={(e) => upd("status", e.target.value)} className="block w-full border border-border px-3 py-2 text-sm mt-1 bg-card">
              {(opts.statuses.length ? opts.statuses : Object.keys(STATUS_LABELS)).map((s) => <option key={s} value={s}>{STATUS_LABELS[s] || s}</option>)}
            </select>
          </div>
          <div>
            <label className="overline text-muted-foreground">Canal</label>
            <select data-testid="contact-channel" value={form.channel} onChange={(e) => upd("channel", e.target.value)} className="block w-full border border-border px-3 py-2 text-sm mt-1 bg-card">
              <option value="">—</option>{opts.channels.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <Field label="Campagne" v={form.campaign_tag} set={(v) => upd("campaign_tag", v)} testid="contact-campaign" />
          <div>
            <label className="overline text-muted-foreground">Assigné à</label>
            <select data-testid="contact-assignee" value={form.assignee_user_id} onChange={(e) => upd("assignee_user_id", e.target.value)} className="block w-full border border-border px-3 py-2 text-sm mt-1 bg-card">
              {team.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </div>
          <div className="col-span-2">
            <label className="overline text-muted-foreground">Dernier message (extrait, 280c)</label>
            <textarea data-testid="contact-snippet" maxLength={280} value={form.last_message_snippet} onChange={(e) => upd("last_message_snippet", e.target.value)} rows={2} className="block w-full border border-border px-3 py-2 text-sm mt-1 outline-none" />
          </div>
          <button data-testid="contact-save" onClick={save} disabled={saving} className="col-span-2 py-2.5 text-sm font-semibold aeliam-btn-primary disabled:opacity-50">{saving ? "Enregistrement…" : isNew ? "Créer le contact" : "Enregistrer"}</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, v, set, testid }) {
  return (
    <div>
      <label className="overline text-muted-foreground">{label}</label>
      <input data-testid={testid} value={v} onChange={(e) => set(e.target.value)} className="block w-full border border-border px-3 py-2 text-sm mt-1 outline-none" />
    </div>
  );
}
